<script setup>
/**
 * Anchor role / CEO ceiling explainer + confirmation (PD-1258).
 *
 * The ONLY surface that changes anchor state — the row menu and the framework
 * views all route through here, so a governance change is never a silent toggle.
 * Explains what the flag is, what it does, and what happens if you change it,
 * then captures the rationale that makes the choice defensible at audit.
 *
 * Ported from the design prototype's LvlAnchorModal (lvl/anchors.jsx).
 */
import { ref, computed, onMounted } from 'vue';
import { LEVELLING_ANCHOR, anchorEventLabel, anchorPreviousRationale } from '@rm-ui/levelling/levellingMeta';

const props = defineProps({
  /** A control-centre row (carries .levelling, .doc_id, .doc_name). */
  role: { type: Object, required: true },
  /** 'anchor' | 'ceo' */
  kind: { type: String, default: 'anchor' },
  /** The markRaw'd levellingAnchors() self object. */
  anchors: { type: Object, required: true },
  /**
   * DRAFT mode — the in-journey path (PD-1258). When present the modal edits an
   * evaluation's draft intent rather than writing an anchor: `set(note)` records the
   * intent, `clear()` drops it, and the flag is applied when the evaluation SAVES.
   * That is what lets one surface both explain the change (a modal, as the anchor
   * story requires) and defer the write to save (as the summary AC requires).
   */
  draftCtx: { type: Object, default: null },
});

const emit = defineEmits(['close', 'saved', 'evaluate']);

const docId = computed(() => Number(props.role.doc_id));
const title = computed(() => props.role.doc_title || props.role.doc_name || props.role.system_number || 'this role');

const isDraft = computed(() => !!props.draftCtx);
/* In draft mode the "current state" is the draft's intent, not a stored anchor. */
const info = computed(() => (props.draftCtx
  ? (props.draftCtx.on ? { anchor: true, note: props.draftCtx.note } : null)
  : props.anchors.anchorInfo(docId.value)));
const isAnchor = computed(() => !!info.value);
const isCeiling = computed(() => !!(info.value && info.value.isCeiling));
const ceiling = computed(() => props.anchors.ceiling.value);
const canGovern = computed(() => props.anchors.canGovern.value);
const saving = computed(() => props.anchors.saving.value);

/** Anchors need a completed FULL evaluation — a classification can't be one. */
/* In the journey the evaluation being summarised IS the qualifying evaluation. */
const eligible = computed(() => (isDraft.value ? true : props.anchors.isEligible(props.role)));
const levelCode = computed(() => (props.role.levelling && props.role.levelling.level_code) || null);

const note = ref('');
const history = ref([]);
const historyLoaded = ref(false);

onMounted(async () => {
  note.value = (props.draftCtx ? props.draftCtx.note : (info.value && info.value.rationale)) || '';
  // A draft has no anchor history yet, and the role's own trail belongs to the record.
  if (!isDraft.value) history.value = await props.anchors.history(docId.value);
  historyLoaded.value = true;
});

const storedNote = computed(() => (props.draftCtx
  ? props.draftCtx.note : (info.value && info.value.rationale)) || '');
const noteChanged = computed(() => note.value.trim() !== storedNote.value.trim());
/* RECOMMENDED, never required (Sasha, Aug — this reverts a brief spell where it was
   required). It is the artefact that makes the reference defensible later, so we nag
   for it; but a blocked anchor is worse than an unexplained one, and a reason typed
   only to clear a gate is not evidence of anything. `noteMissing` therefore drives the
   soft prompt below and NOTHING disables on it. */
const noteMissing = computed(() => !note.value.trim());

function draftSave() {
  if (!props.draftCtx) return;
  props.draftCtx.set(note.value.trim());
  emit('close');
}
function draftClear() {
  if (!props.draftCtx) return;
  props.draftCtx.clear();
  emit('close');
}

/** The rationale in force before history row `i` (shared derivation). */
function previousRationale(i) {
  return anchorPreviousRationale(history.value, i);
}

/* The CEO ceiling lives only on the ceiling holder's record: while unset it
   offers itself on any anchor, and once set it disappears everywhere else, so
   reassigning means removing it from the current holder first. */
const heading = computed(() => {
  if (props.kind === 'ceo') return isCeiling.value ? 'Remove the CEO ceiling?' : 'Set as the CEO ceiling?';
  if (isDraft.value) {
    if (!isAnchor.value) return 'Mark as an anchor role?';
    return storedNote.value.trim() ? 'Anchor role rationale' : 'Add the anchor rationale';
  }
  return isAnchor.value ? 'Remove anchor status?' : 'Mark as an anchor role?';
});

const setDate = computed(() => {
  const at = info.value && info.value.setDate;
  if (!at) return null;
  const d = new Date(at);
  return isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
});

const eventLabel = (row) => {
  const meta = anchorEventLabel(row);
  return meta ? meta.label : row.event;
};
const eventWhen = (row) => {
  const d = new Date(row.audit_date);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

async function apply(patch) {
  const ok = await props.anchors.setAnchor(docId.value, patch);
  if (ok) emit('saved');
}

const markAnchor = () => apply({ anchor: true, rationale: note.value });
const removeAnchor = () => apply({ anchor: false });
const saveNote = () => apply({ rationale: note.value });
const setCeiling = () => apply({ ceiling: true });
const removeCeiling = () => apply({ ceiling: false });
</script>

<template>
  <div class="lvl-modal-scrim" @click.self="emit('close')">
    <div class="lvl-modal lvl-anchor-modal" role="dialog" aria-modal="true" :aria-label="heading">
      <div class="lvl-modal-head">
        <h3>{{ heading }}</h3>
        <button class="lvl-modal-x" aria-label="Close" @click="emit('close')">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="lvl-modal-body lvl-anchor-mbody">
        <!-- ===== CEO ceiling ===== -->
        <template v-if="kind === 'ceo'">
          <div class="lvl-anchor-msec">
            <span class="lvl-anchor-msec-ic"><v-icon size="17">{{ LEVELLING_ANCHOR.ceiling.mdi }}</v-icon></span>
            <div class="lvl-anchor-msec-main">
              <span class="h">What it is</span>
              <span class="b">
                The CEO ceiling marks your most senior role and fixes the top of the levelling framework<template
                  v-if="isCeiling"
                > — currently {{ levelCode || 'this role’s level' }}, held by this role</template><template
                  v-else-if="ceiling"
                > — currently {{ ceiling.levelCode }}, held by {{ ceiling.title }}</template>.
              </span>
            </div>
          </div>
          <div class="lvl-anchor-msec">
            <span class="lvl-anchor-msec-ic"><v-icon size="17">mdi-gavel</v-icon></span>
            <div class="lvl-anchor-msec-main">
              <span class="h">What it does</span>
              <span class="b">
                Classification stops offering levels at or above the ceiling, and any evaluation landing
                there is flagged for review — never blocked — so nothing quietly outranks the top of your
                organisation.
              </span>
            </div>
          </div>
          <div class="lvl-anchor-msec">
            <span class="lvl-anchor-msec-ic"><v-icon size="17">{{ isCeiling ? 'mdi-undo' : 'mdi-arrow-down' }}</v-icon></span>
            <div class="lvl-anchor-msec-main">
              <span class="h">{{ isCeiling ? 'If you remove it' : 'If you set it' }}</span>
              <span v-if="isCeiling" class="b">
                Classification offers every level again and above-ceiling evaluations are no longer
                flagged, until a new ceiling is set. Recorded in this role’s history.
              </span>
              <span v-else class="b">
                <template v-if="ceiling">The ceiling moves here from {{ ceiling.title }} — recorded on both roles. </template>
                The framework top becomes {{ levelCode || 'this role’s level' }}. Recorded in this role’s
                history and reversible.
              </span>
            </div>
          </div>
        </template>

        <!-- ===== Anchor role ===== -->
        <template v-else>
          <div class="lvl-anchor-msec">
            <span class="lvl-anchor-msec-ic"><v-icon size="17">{{ LEVELLING_ANCHOR.anchor.mdi }}</v-icon></span>
            <div class="lvl-anchor-msec-main">
              <span class="h">What it is</span>
              <span class="b">
                An anchor role is a fully evaluated reference the rest of the register calibrates against
                — a “known-good” level for comparable roles.<template v-if="isAnchor">
                  Set by {{ (info && info.setByName) || '—' }}<template v-if="setDate"> on {{ setDate }}</template>.
                </template>
              </span>
            </div>
          </div>

          <div v-if="isAnchor && info.rationale" class="lvl-anchor-msec">
            <span class="lvl-anchor-msec-ic"><v-icon size="17">mdi-format-quote-close</v-icon></span>
            <div class="lvl-anchor-msec-main">
              <span class="h">Recorded rationale</span>
              <span class="b">{{ info.rationale }}</span>
            </div>
          </div>

          <div class="lvl-anchor-msec">
            <span class="lvl-anchor-msec-ic"><v-icon size="17">mdi-scale-balance</v-icon></span>
            <div class="lvl-anchor-msec-main">
              <span class="h">Why it matters</span>
              <span class="b">
                Anchors keep levelling consistent between people and job families, and give compliance a
                defensible record of how levels were set — the EU pay-transparency directive expects
                classification to rest on evaluated reference roles.
              </span>
            </div>
          </div>

          <div class="lvl-anchor-msec">
            <span class="lvl-anchor-msec-ic">
              <v-icon size="17">{{ isAnchor ? 'mdi-undo' : eligible ? 'mdi-check-circle-outline' : 'mdi-clipboard-alert-outline' }}</v-icon>
            </span>
            <div class="lvl-anchor-msec-main">
              <span class="h">{{ isDraft && isAnchor ? 'If you save it'
                : isAnchor ? 'If you remove it' : eligible ? 'If you mark it' : 'Not yet eligible' }}</span>
              <!-- Draft mode is about what SAVING will do, not about removing a stored
                   anchor — nothing has been written yet. -->
              <span v-if="isDraft && isAnchor" class="b">
                This evaluation becomes the calibration reference for its family — marked in the control
                centre, calibration and compare, and leading its band. The rationale is stored with it and
                recorded in this role’s history.
              </span>
              <span v-else-if="isAnchor" class="b">
                This family loses its calibration reference<template v-if="isCeiling">, and the CEO
                ceiling set on it is removed too</template>. Nothing else changes on the role. Recorded in
                its history; you can re-mark it any time.
              </span>
              <span v-else-if="eligible" class="b">
                It appears with the anchor mark in the control centre, calibration and compare, and leads
                its band as the reference. Recorded in this role’s history; reversible.
              </span>
              <span v-else class="b">
                Anchors must carry a full point-scored evaluation — this role is
                {{ levelCode ? 'classified, not evaluated' : 'not yet levelled' }}. Run a full evaluation
                and tick “Save as an anchor role” on the summary.
              </span>
            </div>
          </div>

          <!-- Rationale: recommended, never required. -->
          <div v-if="canGovern && (isAnchor || eligible)" class="lvl-anchor-note">
            <label for="cc-anchor-note">
              Why is this an anchor? <span class="opt">Recommended</span>
            </label>
            <textarea
              id="cc-anchor-note"
              v-model="note"
              class="lvl-textarea"
              rows="3"
              placeholder="e.g. Most senior individual contributor in the family; scope agreed with the reward panel, so it is a safe reference for comparable roles."
            ></textarea>
            <span class="hint">
              <template v-if="isAnchor && !note.trim()">
                No rationale recorded. Without one, nobody can tell later why this role was chosen — add a
                line so it holds up at audit.
              </template>
              <template v-else>
                Captured with the anchor and shown wherever it is used as a reference. Amending it is
                recorded in the role’s history.
              </template>
            </span>
          </div>
        </template>

        <!-- Audit trail -->
        <div v-if="!isDraft && historyLoaded && history.length" class="lvl-anchor-hist">
          <span class="lvl-anchor-hist-h">Change history</span>
          <div v-for="(row, i) in history" :key="i" class="lvl-anchor-hist-row">
            <span class="t">{{ eventLabel(row) }}</span>
            <span class="s">{{ [row.created_by_name, eventWhen(row)].filter(Boolean).join(' · ') }}</span>
            <span v-if="row.rationale" class="q">“{{ row.rationale }}”</span>
            <!-- "Rationale updated" alone doesn't say what it changed FROM, which is
                 the question an auditor asks. Derived from the next-older event that
                 carried a rationale — see anchorPreviousRationale. -->
            <span v-if="row.event === 'ANCHOR_NOTE'" class="was">
              <template v-if="previousRationale(i)">was “{{ previousRationale(i) }}”</template>
              <template v-else>no rationale was recorded before this</template>
            </span>
          </div>
        </div>

        <div class="lvl-anchor-mfoot">
          <v-icon size="13">mdi-history</v-icon>
          Every change is audited (who, when) in the role’s history.
        </div>
      </div>

      <div class="lvl-modal-foot">
        <v-btn text small @click="emit('close')">Cancel</v-btn>

        <template v-if="!canGovern">
          <span class="lvl-anchor-readonly">You don’t have permission to change anchor roles.</span>
        </template>

        <!-- CEO ceiling actions -->
        <template v-else-if="kind === 'ceo'">
          <v-btn v-if="isCeiling" small outlined color="error" :loading="saving" @click="removeCeiling">
            Remove ceiling
          </v-btn>
          <v-btn v-else small color="primary" :loading="saving" @click="setCeiling">
            <v-icon size="16" left>{{ LEVELLING_ANCHOR.ceiling.mdi }}</v-icon>
            Set as ceiling
          </v-btn>
        </template>

        <!-- Anchor actions -->
        <!-- DRAFT (in-journey): edits the evaluation's intent; the flag is applied
             when the evaluation saves, never from here. -->
        <template v-else-if="isDraft">
          <v-btn v-if="isAnchor" small outlined color="error" @click="draftClear">
            Don’t anchor this role
          </v-btn>
          <v-btn
            small
            color="primary"
            :disabled="isAnchor && !noteChanged"
            @click="draftSave"
          >
            <v-icon size="16" left>{{ LEVELLING_ANCHOR.anchor.mdi }}</v-icon>
            {{ isAnchor ? (storedNote.trim() ? 'Update rationale' : 'Save rationale') : 'Mark as anchor' }}
          </v-btn>
        </template>

        <template v-else-if="isAnchor">
          <v-btn v-if="noteChanged" small outlined :loading="saving" @click="saveNote">
            {{ info.rationale ? 'Update rationale' : 'Save rationale' }}
          </v-btn>
          <v-btn small outlined color="error" :loading="saving" @click="removeAnchor">Remove anchor</v-btn>
        </template>
        <template v-else-if="eligible">
          <v-btn
            small
            color="primary"
            :loading="saving"
            @click="markAnchor"
          >
            <v-icon size="16" left>{{ LEVELLING_ANCHOR.anchor.mdi }}</v-icon>
            Mark as anchor
          </v-btn>
        </template>
        <template v-else>
          <v-btn small color="primary" @click="emit('evaluate')">
            <v-icon size="16" left>mdi-clipboard-check-outline</v-icon>
            Evaluate to anchor
          </v-btn>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.lvl-modal-scrim {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(16, 24, 40, 0.44);
  display: grid;
  place-items: center;
  padding: 24px;
}

.lvl-modal {
  width: min(560px, 100%);
  max-height: min(86vh, 760px);
  display: flex;
  flex-direction: column;
  background: var(--cc-surface, #fff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(16, 24, 40, 0.22);
  overflow: hidden;
}

.lvl-modal-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 12px;

  h3 {
    flex: 1;
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: var(--cc-text, #1f242d);
    line-height: 1.35;
  }
}

.lvl-modal-x {
  flex: none;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--cc-text-3, #97a0af);
  padding: 2px;

  &:hover { color: var(--cc-text, #1f242d); }
}

.lvl-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 4px 20px 18px;
}

.lvl-modal-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 20px 16px;
  border-top: 1px solid var(--cc-border-soft, #edf0f7);
}

/* ---- ported from the prototype's lvl.css anchor block ---- */
.lvl-anchor-mbody {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.lvl-anchor-msec {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.lvl-anchor-msec-ic {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 9%, transparent);
  color: var(--cc-accent, #1d5bd6);

  ::v-deep(.v-icon) { color: currentColor !important; }
}

.lvl-anchor-msec-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  .h {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: var(--cc-text, #1f242d);
  }

  .b {
    font-size: 13px;
    color: var(--cc-text-2, #525c6b);
    line-height: 1.5;
  }
}

.lvl-anchor-note {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  background: var(--cc-canvas, #f6f7f9);

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    font-weight: 700;
    color: var(--cc-text, #1f242d);
  }

  .opt {
    font-size: 10.5px;
    font-weight: 650;
    color: var(--cc-text-3, #97a0af);
    border: 1px solid var(--cc-border, #e5e7eb);
    border-radius: 999px;
    padding: 1px 7px;
  }

  textarea {
    width: 100%;
    resize: vertical;
    min-height: 64px;
    padding: 8px 10px;
    border: 1px solid var(--cc-border, #e5e7eb);
    border-radius: 8px;
    background: var(--cc-surface, #fff);
    color: var(--cc-text, #1f242d);
    font: inherit;
    font-size: 13px;

    &:focus {
      outline: none;
      border-color: var(--cc-accent, #1d5bd6);
    }
  }

  .hint {
    font-size: 11.5px;
    color: var(--cc-text-3, #97a0af);
    line-height: 1.45;
  }
}

.lvl-anchor-hist {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--cc-border-soft, #edf0f7);

  .lvl-anchor-hist-h {
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--cc-text-3, #97a0af);
  }
}

.lvl-anchor-hist-row {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding-left: 10px;
  border-left: 2px solid var(--cc-accent, #1d5bd6);

  .t {
    font-size: 12.5px;
    font-weight: 650;
    color: var(--cc-text, #1f242d);
  }

  .s {
    font-size: 11.5px;
    color: var(--cc-text-3, #97a0af);
  }

  .q {
    font-size: 12px;
    font-style: italic;
    color: var(--cc-text-2, #525c6b);
    line-height: 1.45;
  }

  /* The superseded value: quieter than the current one, so the row reads
     "this is what it says now" first and "this is what it replaced" second. */
  .was {
    font-size: 11.5px;
    font-style: italic;
    color: var(--cc-text-3, #97a0af);
    line-height: 1.45;
  }
}

.lvl-anchor-mfoot {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  padding-top: 12px;
  border-top: 1px solid var(--cc-border-soft, #edf0f7);
  font-size: 11.5px;
  color: var(--cc-text-3, #97a0af);

  ::v-deep(.v-icon) { color: currentColor !important; }
}

.lvl-anchor-readonly {
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
}

</style>
